import React, { useState, useEffect } from 'react';
import FloorPlan from './FloorPlan';
import BookingPanel from './BookingPanel';
import QRBooking from './QRBooking';
import RoomSchedule from './RoomSchedule';
import DatePicker from './DatePicker';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * KioskView - Portrait layout for the UniFi 27" lobby touchscreen.
 *
 * Layout (top to bottom):
 *   Header (light blue, logo + date picker + weather + clock)
 *   Calendar section (dark, 50% of remaining) - conference/meeting/huddle cards
 *   Floor plan map (light, 50% of remaining)
 *   Offices summary (light, auto-sized)
 *   Cubicles summary (light, auto-sized)
 *
 * Idle timer in App.jsx resets to default state after 30s of no interaction.
 */
export default function KioskView({
  officeConfig,
  availability,
  selectedRoom,
  selectedDate,
  onDateChange,
  onRoomSelect,
  onBook,
  showQR,
  onCloseQR,
  error,
  onDismissError,
  bookingSuccess,
  onDismissSuccess,
  touchDisabled = false,
}) {
  // When the touchscreen is out of service, the kiosk becomes a read-only
  // status board: no room can be selected and all booking controls are hidden.
  const roomSelect = touchDisabled ? undefined : onRoomSelect;

  return (
    <div className={`kiosk-view${touchDisabled ? ' kiosk-view--touch-disabled' : ''}`}>
      {/* Kiosk Header */}
      <header className="kiosk-header">
        <div className="kiosk-header-left">
          <img
            src={officeConfig.theme?.logo || '/logo-full.png'}
            alt={officeConfig.theme?.logoAlt || 'SSP Innovations'}
            className="header-logo"
          />
          <span className="kiosk-header-subtitle">
            {officeConfig.name} Office{officeConfig.theme?.subtitleTag ? ` | ${officeConfig.theme.subtitleTag}` : ''}
          </span>
        </div>

        {!touchDisabled && (
          <div className="kiosk-header-center">
            <DatePicker
              selectedDate={selectedDate}
              onDateChange={onDateChange}
              isKiosk={true}
            />
          </div>
        )}

        <div className="kiosk-header-right">
          <KioskWeather officeId={officeConfig.id} />
          <div className="kiosk-header-divider" />
          <KioskClock />
        </div>
      </header>

      {/* Out-of-service banner: touchscreen unavailable, direct users elsewhere */}
      {touchDisabled && (
        <div className="kiosk-touch-banner" role="status">
          <span className="kiosk-touch-icon" aria-hidden="true">&#9888;</span>
          <div className="kiosk-touch-banner-text">
            <strong>Touchscreen temporarily unavailable</strong>
            <span>This screen shows current room status only. To book a room or desk, please use the Front Kiosk.</span>
          </div>
        </div>
      )}

      {/* Notifications */}
      {error && (
        <div className="kiosk-notification kiosk-notification--error">
          {error}
          <button onClick={onDismissError}>Dismiss</button>
        </div>
      )}
      {bookingSuccess && (
        <div className="kiosk-notification kiosk-notification--success">
          <strong>Booking Confirmed!</strong> {bookingSuccess.roomName}
          <button onClick={onDismissSuccess}>OK</button>
        </div>
      )}

      {/* Main kiosk content */}
      <main className="kiosk-main">
        {/* Calendar section (dark background, 50% of flexible space) */}
        <div className="kiosk-calendar">
          <RoomSchedule
            availability={availability}
            onRoomSelect={roomSelect}
            section="calendar"
          />
        </div>

        {/* Floor plan map (splits remaining space with calendar) */}
        <div className="kiosk-map">
          <FloorPlan
            officeConfig={officeConfig}
            availability={availability}
            onRoomSelect={roomSelect}
            selectedRoom={selectedRoom}
            isKiosk={true}
          />
        </div>

        {/* Offices and Cubicles (auto-sized, stacked) */}
        <div className="kiosk-desks">
          <RoomSchedule
            availability={availability}
            onRoomSelect={roomSelect}
            section="offices"
          />
          <RoomSchedule
            availability={availability}
            onRoomSelect={roomSelect}
            section="cubicles"
          />
        </div>
      </main>

      {/* Booking Panel overlay */}
      {!touchDisabled && selectedRoom && (
        <div className="kiosk-panel-overlay">
          <BookingPanel
            selectedRoomId={selectedRoom}
            availability={availability}
            onBook={onBook}
            onCancel={() => onRoomSelect?.(null)}
            isKiosk={true}
            existingBooking={null}
            selectedDate={selectedDate}
          />
        </div>
      )}

      {/* QR Code modal */}
      {!touchDisabled && showQR && (
        <QRBooking
          bookingDetails={showQR}
          baseUrl={window.location.origin}
          office={officeConfig.id}
          onClose={onCloseQR}
        />
      )}
    </div>
  );
}

function KioskClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="kiosk-clock">
      <span className="kiosk-clock-time">
        {time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
      </span>
      <span className="kiosk-clock-date">
        {time.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
      </span>
    </div>
  );
}

// OpenWeatherMap icon code to emoji mapping
const WEATHER_ICONS = {
  '01d': '☀️',     // clear sky day - sun
  '01n': '🌙',     // clear sky night - crescent moon
  '02d': '⛅',           // few clouds day - sun behind cloud
  '02n': '🌙',     // few clouds night
  '03d': '☁️',     // scattered clouds - cloud
  '03n': '☁️',
  '04d': '☁️',     // broken clouds
  '04n': '☁️',
  '09d': '🌧️', // shower rain
  '09n': '🌧️',
  '10d': '🌦️', // rain day - sun behind rain
  '10n': '🌧️', // rain night
  '11d': '⛈️',     // thunderstorm
  '11n': '⛈️',
  '13d': '❄️',     // snow
  '13n': '❄️',
  '50d': '🌫️', // mist/fog
  '50n': '🌫️',
};

function KioskWeather({ officeId }) {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    async function fetchWeather() {
      try {
        const res = await fetch(`${API_BASE}/weather?office=${officeId || 'wauwatosa'}`);
        if (res.ok) {
          const data = await res.json();
          setWeather(data);
        }
      } catch (err) {
        console.warn('Weather fetch failed:', err);
      }
    }
    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000); // Refresh every 15 min
    return () => clearInterval(interval);
  }, [officeId]);

  if (!weather) return null;

  const icon = WEATHER_ICONS[weather.icon] || '☁️';

  return (
    <div className="kiosk-weather">
      <span className="kiosk-weather-icon">{icon}</span>
      <div className="kiosk-weather-info">
        <span className="kiosk-weather-temp">{Math.round(weather.temp)}&deg;F</span>
        <span className="kiosk-weather-desc">{weather.description}</span>
      </div>
    </div>
  );
}
